import { ChevronUp, SquareArrowOutUpRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import defaultAvatar from '@/assets/images/avatar-intercom-admin.png';
import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import { Stage } from '@/components/Stage/Stage';
import Typo from '@/components/Typography/Typo';
import FeedbackBlockTooltip from '@/features/Feedback/components/FeedbackBlockTooltip/FeedbackBlockTooltip';
import useFeedbackBlockTooltip from '@/features/Feedback/components/FeedbackBlockTooltip/useFeedbackBlockTooltip';
import { cn } from '@/lib/cn';
import { ContentStyle } from '@/styles/ContentStyle';
import { FEEDBACK_DETAIL_MODAL_SCROLL_SELECTOR } from '@/types/feedbackConst';
import { filterMap } from '@/utils/lazy';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import feedbackDetailUtils from '../../utils/feedbackDetailutils';
import useContentBodyConfig from '../useContentBodyConfig';

type Props = {
  detail: FeedbackT;
  focusedBlock?: string;
  feedbackDetailFullView: boolean;
  feedbackAlias: string;
};

export const ThreadDisplay = (props: Props) => {
  const rootChat = props.detail.chat_text?.filter(
    (chat) => !chat.parent_source_id,
  );

  const { chatRef, selectedChat } = useContentBodyConfig({
    isFullView: props.feedbackDetailFullView,
    chat_messages: props.detail?.chat_text,
    scrollSelector: `#${FEEDBACK_DETAIL_MODAL_SCROLL_SELECTOR(
      props.feedbackAlias,
    )}`,
    blocks: props.detail?.blocks || [],
    sentences: props.detail?.sentences || [],
  });

  return (
    <VFlex ref={chatRef}>
      {rootChat?.map((chat) => {
        return (
          <ThreadItem
            chat={chat}
            key={chat.source_id}
            detail={props.detail}
            focusedBlock={props.focusedBlock}
            focused={selectedChat === chat.source_id}
          />
        );
      })}
    </VFlex>
  );
};

const MAX_REPLIES_TO_SHOW = 4;

const ThreadItem = (props: {
  chat: ChatMessageT;
  detail: FeedbackT;
  focusedBlock?: string;
  focused: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const childrenChat = props.detail.chat_text?.filter(
    (chat) => chat.parent_source_id === props.chat.source_id,
  );

  const focusedBlock = props.detail.blocks?.find(
    (block) => block.alias === props.focusedBlock,
  );

  const hasFocusedBlock = focusedBlock
    ? focusedBlock.locations.some((location) =>
        childrenChat?.some(
          (chat) => chat.source_id === location.message_source_id,
        ),
      )
    : false;

  const [childrenOpen, setChildrenOpen] = useState(hasFocusedBlock);
  const textHTML = useMemo(() => {
    return feedbackDetailUtils
      .setBlockTags({
        text: props.chat.text,
        blocks: props.detail.blocks || [],
        source_id: props.chat.source_id,
        focusedBlock: props.focusedBlock,
      })
      .replaceAll('\n', '<br/>');
  }, [
    props.detail.blocks,
    props.focusedBlock,
    props.chat.source_id,
    props.chat.text,
  ]);

  const { tooltipInfo, currentBlockInfo, onMouseMove, onMouseLeave } =
    useFeedbackBlockTooltip({
      blocks: props.detail.blocks || [],
    });

  const childrenUniqueAuthor = useMemo(() => {
    return filterMap(
      Array.from(
        new Set(
          filterMap(childrenChat || [], (chat) => {
            if (!chat.author?.ID) return null;
            return chat.author.ID;
          }),
        ),
      ),
      (id) => {
        return childrenChat?.find((chat) => chat.author?.ID === id);
      },
    );
  }, [childrenChat]);

  useEffect(() => {
    setTimeout(() => {
      if (props.focused && ref.current) {
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }, 100);
  }, [props.focused]);

  return (
    <div
      className={cn(
        'flex items-start gap-2.5 py-2.5 px-2 -mx-2 rounded-md hover:bg-gray-100 transition-all duration-150',
        props.focused && 'animate-bounce repeat-4',
      )}
      ref={ref}
    >
      <div className="size-8.5 rounded-lg overflow-hidden bg-gray-200">
        <ImageContainer
          src={props.chat.author?.profile_url || ''}
          fallback={defaultAvatar}
          alt={props.chat.author?.name || ''}
        />
      </div>
      <div className="flex-1 flex-col">
        <div className="relative group/thread-item">
          <div className="absolute top-0 right-0 opacity-0 group-hover/thread-item:opacity-100 transition-all duration-200">
            <a href={props.chat.link} target="_blank">
              <SquareArrowOutUpRight className="size-4 text-gray-600" />
            </a>
          </div>
          <Flex d="column" g={4}>
            <div className="flex items-center gap-1">
              <Typo type="label2" weight="sb">
                {props.chat.author?.name}
              </Typo>
              <Typo type="label3" className="text-gray-600">
                {getTimezoneDayjs(props.chat.created_at).format(
                  DATE_FORMAT.timeOnly,
                )}
              </Typo>
            </div>

            <ContentStyle
              onMouseLeave={onMouseLeave}
              onMouseMove={onMouseMove}
              dangerouslySetInnerHTML={{
                __html: textHTML,
              }}
            />
          </Flex>
          <FeedbackBlockTooltip
            tooltipInfo={tooltipInfo}
            currentBlockInfo={currentBlockInfo}
          />
        </div>
        {childrenChat && childrenChat.length > 0 && (
          <>
            <Space h={8} />
            <Stage>
              <Stage.Scene status={childrenOpen}>
                <VFlex>
                  {childrenChat?.map((chat) => {
                    return (
                      <ThreadItem {...props} chat={chat} key={chat.source_id} />
                    );
                  })}
                  <Button
                    leftIcon={<ChevronUp size={14} />}
                    sizeType="s"
                    variant="ghost"
                    onClick={() => setChildrenOpen(false)}
                    className="w-fit"
                  >
                    Hide {childrenChat?.length} replies
                  </Button>
                </VFlex>
              </Stage.Scene>
              <Stage.Scene default status={!childrenOpen}>
                <div
                  onClick={() => setChildrenOpen(true)}
                  className="flex gap-2 items-center transition-all duration-200 cursor-pointer p-1 pr-3 rounded-md hover:bg-gray-200 w-fit"
                >
                  <div className="flex items-center gap-1">
                    {childrenUniqueAuthor
                      ?.slice(0, MAX_REPLIES_TO_SHOW)
                      .map((chat, index, array) => {
                        return (
                          <div
                            key={chat.source_id}
                            className="relative size-6 rounded-md overflow-hidden bg-gray-200"
                          >
                            {index === array.length - 1 &&
                              array?.length > MAX_REPLIES_TO_SHOW && (
                                <div className="absolute top-0 right-0 size-6 bg-gray-800/60 flex items-center justify-center z-10">
                                  <Typo
                                    type="body2"
                                    weight="sb"
                                    className="text-gray-100"
                                  >
                                    +{array?.length - MAX_REPLIES_TO_SHOW}
                                  </Typo>
                                </div>
                              )}
                            <picture>
                              <source
                                srcSet={chat.author?.profile_url}
                                type="image/webp"
                              />
                              <img
                                className="size-full object-cover"
                                src="/assets/avatar-intercom-admin.png"
                                alt={chat.author?.name}
                              />
                            </picture>
                          </div>
                        );
                      })}
                  </div>
                  <Typo type="body3" weight="m" className="text-gray-700">
                    {childrenChat?.length} replies
                  </Typo>
                </div>
              </Stage.Scene>
            </Stage>
          </>
        )}
      </div>
    </div>
  );
};

const ImageContainer = (props: {
  src: string;
  fallback: string;
  alt: string;
}) => {
  const [error, setError] = useState(false);
  return (
    <picture>
      {!error && <source srcSet={props.src} type="image/webp" />}
      <img
        src={error ? props.fallback : props.src}
        alt={props.alt}
        className="size-full object-cover border border-gray-300"
        onError={() => setError(true)}
      />
    </picture>
  );
};
